// Motor del tiempo real, sin DOM ni red: de los minutos de llegada de las paradas a los buses
// que se dibujan y cómo se mueven entre consulta y consulta. Lo usan la app (cuando consulta
// ella misma la API) y el servicio `server/` (que consulta todas las líneas sin parar y manda
// el resultado a las apps). Las posiciones son ESTIMADAS: la API no publica el GPS de los buses.

import type { Dataset, LineId } from "../data";
import { haversine, type LatLng } from "../geo";
import type { ApiEstimation } from "./api";
import { fromRoutePath, pointAt, type RouteGeom } from "./geometry";
import {
  DEFAULT_INFER,
  distanceAt,
  inferBuses,
  normDest,
  type InferredBus,
  type LiveRoute,
  type StopEta,
} from "./infer";

export const EXTRAPOLATE_MS = 60_000; // confianza «media» si el dato es más reciente que esto
// Hasta cuándo sigue avanzando un bus con un dato que no se renueva. Con la red entera cada
// parada se relee cada ~1 min, y la evidencia de un bus (su lectura más antigua) suele tener
// ya 60 s: con el límite en 60 s se quedaba congelado casi la mitad del tiempo. El tope de su
// próxima parada sigue acotando hasta dónde llega.
export const MOVE_MS = 90_000;
export const EVIDENCE_STALE_MS = 150_000;
export const BLEND_MS = 3000; // al llegar datos nuevos el bus se desliza a su sitio, no salta…
const BLEND_MS_SPEED = 12; // …a esta velocidad (m/s) como mucho, si está lejos
const MATCH_MAX_M = 700; // entre variantes distintas: cercanía en el mapa
const BACK_MAX_M = 1500; // mismo trayecto: hasta cuánto por detrás aceptamos que es el mismo bus
const AHEAD_MAX_M = 2500; // … y por delante (una consulta tardía)
const MAX_MISSES = 1;
const TURN_STOP_M = 200; // paradas de llegada y salida de una cabecera
const TURN_NEAR_M = 300; // bus «saliendo»: a menos de esto del principio de su trayecto
const TURN_SLACK_S = 90;
const AT_STOP_S = 8; // con «0 min» en su próxima parada, el bus llega a ella en esto como mucho
const DWELL_S = 15; // …para allí esto y sigue hacia la parada siguiente…
// …si la API espera al bus en la siguiente en al menos esto (min, ya descontada la antigüedad:
// un «1 min» de hace menos de 30 s; un «0» no basta).
const DEPART_NEXT_MIN = 0.5;
const NEAR_TWIN_M = 400; // un bus que los datos ya no ven y tiene otro de su trayecto a menos: es ese
const SPEED_TAU_S = 90; // constante de tiempo de la velocidad suavizada del bus
const BAND_S = 30; // medio minuto: incertidumbre de un minuto entero de la API

export interface RouteEntry {
  route: LiveRoute;
  geom: RouteGeom;
}
interface LineEntry {
  routes: RouteEntry[];
  // Por trayecto: destinos de los demás trayectos (sentido contrario y otras variantes del
  // mismo sentido). La API anuncia cada bus con el destino de su variante, así que cada
  // trayecto solo usa los minutos de sus propios autobuses.
  exclude: Map<string, Set<string>>;
}

// Salida de una parada tras «0 min»: desde `at` (ms) avanza a `speed` (m/s) hasta `cap`
// (metros del trayecto). `from` es la parada de la que sale y `arrived` cuándo llegó a ella.
export interface Departure {
  at: number;
  speed: number;
  cap: number;
  from: number;
  arrived: number;
}

// Estado de un bus tal como viaja del servidor a la app (sin el trazado, que la app ya tiene).
export interface BusState {
  id: string;
  num?: number | undefined; // número estable del bus en su línea (lo pone el servidor)
  bus: InferredBus;
  polledAt: number; // instante al que se refieren los minutos de la API
  shownAt: number; // cuando llegó esta posición al mapa (inicio de la transición)
  // Continuidad. Un bus recién visto va hasta su próxima parada. Desde la segunda consulta
  // avanza a velocidad constante (`speed`, m/s) desde donde se ve (`base`, metros del
  // trayecto en `shownAt`): la que lo lleva a donde estará en la próxima consulta según lo que
  // ha avanzado de verdad entre las dos últimas. Nunca retrocede. `blendFrom` se usa si cambia
  // de trayecto (variante o media vuelta en la cabecera).
  // `then`: tras «0 min» en su próxima parada, a partir de `at` sale de ella (`cap`) hacia la
  // siguiente a `speed`, sin pasarla (`then.cap`) hasta que la API lo diga.
  track: { base: number; speed: number; cap: number; then?: Departure } | null;
  obsD: number; // posición deducida en la última consulta (metros del trayecto)…
  obsT: number; // …y su instante
  blendFrom: { ll: LatLng; at: number; ms: number } | null;
  lastLL: LatLng | null;
  misses: number; // consultas seguidas sin volver a deducirlo
  // Se dibuja. Un bus que aparece a mitad de recorrido espera a verse en la consulta siguiente:
  // la API a veces da minutos incoherentes entre paradas vecinas («14 19 16 22») que durante una
  // consulta parecen otro bus.
  shown: boolean;
  firstSeen?: number; // cuándo se dedujo por primera vez
  vEst?: number; // velocidad suavizada (m/s) de lo que ha avanzado de verdad entre consultas
  confidence?: "low" | "medium";
  lastEvidenceAt?: number;
}
export interface Tracked extends BusState {
  entry: RouteEntry;
}
export interface Fresh {
  bus: InferredBus;
  entry: RouteEntry;
}

export interface MergeOptions {
  now: number;
  polledAt: number; // instante de los minutos de esta consulta
  nextPollS: number; // segundos hasta la siguiente
  polledLines: ReadonlySet<LineId>; // líneas que ya tenían consulta anterior
  active: ReadonlySet<LineId>;
  newId: () => string;
  // Tiempo que tiene que llevar visto un bus nuevo a mitad de recorrido para dibujarlo. Con
  // consultas espaciadas basta la segunda; si se deduce más a menudo de lo que se refrescan las
  // paradas (el servicio), dos deducciones seguidas pueden salir de los mismos minutos malos.
  confirmMs?: number;
}

export type Engine = ReturnType<typeof createEngine>;

export function createEngine(ds: Dataset) {
  const lines = new Map<LineId, LineEntry>();

  // Trayectos y trazados vienen del dataset (data/routes.json): de la API solo pedimos minutos.
  const loadLine = (line: LineId): LineEntry => {
    const entries: RouteEntry[] = [];
    for (const r of ds.routes.get(line) ?? []) {
      const route: LiveRoute = {
        key: `${line}|${r.id}`,
        line,
        sense: r.sense,
        destination: r.destination,
        stops: r.stops.map((st) => ({
          id: st.id,
          name: ds.stopsById.get(st.id)?.name ?? String(st.id),
          dist: st.dist,
        })),
      };
      const geom = fromRoutePath(
        r.path,
        r.stopVertex,
        r.stops.map((st) => st.dist),
      );
      entries.push({ route, geom });
    }
    const exclude = new Map<string, Set<string>>();
    for (const e of entries) {
      const own = normDest(e.route.destination);
      const other = entries.map((x) => normDest(x.route.destination)).filter((d) => d !== own);
      exclude.set(e.route.key, new Set(other));
    }
    return { routes: entries, exclude };
  };
  const line = (id: LineId): LineEntry => {
    let le = lines.get(id);
    if (!le) lines.set(id, (le = loadLine(id)));
    return le;
  };
  const entryOf = (routeKey: string): RouteEntry | null =>
    line(routeKey.split("|")[0]!).routes.find((e) => e.route.key === routeKey) ?? null;

  // Cabeceras: el final de un trayecto y el principio de otro en la misma parada (o a dos pasos).
  const sameStop = (a: number, b: number): boolean => {
    if (a === b) return true;
    const pa = ds.stopsById.get(a)?.ll;
    const pb = ds.stopsById.get(b)?.ll;
    return !!pa && !!pb && haversine(pa, pb) < TURN_STOP_M;
  };
  const endOf = (e: RouteEntry): { id: number; dist: number } => {
    const s = e.route.stops[e.route.stops.length - 1]!;
    return { id: s.id, dist: s.dist };
  };
  const atHead = (bus: InferredBus, e: RouteEntry): boolean =>
    bus.timeline[0]!.d - e.route.stops[0]!.dist < TURN_NEAR_M;
  // ¿`arr` llega al final de su trayecto justo donde empieza el de `dep`?
  const turnsInto = (arr: RouteEntry, dep: RouteEntry): boolean =>
    arr !== dep && sameStop(endOf(arr).id, dep.route.stops[0]!.id);

  const currentD = (t: Tracked, now: number): number => {
    // La animación no prolonga indefinidamente una observación que no se ha renovado.
    const until = Math.min(now, (t.lastEvidenceAt ?? t.polledAt) + MOVE_MS);
    if (!t.track) return distanceAt(t.bus.timeline, (until - t.polledAt) / 1000);
    const { base, speed, cap, then } = t.track;
    const d = Math.max(base, Math.min(cap, base + (speed * (until - t.shownAt)) / 1000));
    if (!then || d < cap) return d;
    // Sale cuando ha llegado a la parada y ha cumplido la espera, lo último de las dos: si
    // llegase tarde y se contara desde `then.at`, al tocar la parada daría un salto.
    const reached = speed > 0 ? t.shownAt + (1000 * Math.max(0, cap - base)) / speed : t.shownAt;
    const start = Math.max(then.at, reached);
    if (until <= start) return d;
    return Math.max(d, Math.min(then.cap, cap + (then.speed * (until - start)) / 1000));
  };

  // Salida de la parada a la que llega con «0 min»: sin esto se quedaba clavado en ella hasta
  // la consulta siguiente, aunque el bus ya hubiera pasado (y luego saltaba dos paradas).
  // La hora de salida la marca la parada siguiente de su línea temporal: sale a tiempo de
  // llegar a ella cuando dice la API, y nunca antes de DWELL_S tras llegar. Si la siguiente
  // espera al bus en 1 min sale enseguida; si en 5 (de noche algunos esperan su hora en una
  // parada), se queda. Sin dato de la siguiente no sale: se espera a la API, como antes.
  // La API sigue dando «0 min» mientras el bus llega y para: la llegada se cuenta desde la
  // primera vez (`prev`); si no, cada lectura la retrasaba y el bus no salía nunca. En la
  // cabecera tampoco: allí espera su hora de salida.
  const departure = (
    f: Fresh,
    track: { base: number; speed: number; cap: number },
    shownAt: number,
    timelineAt: number,
    prev?: Departure,
  ): Departure | undefined => {
    const tl = f.bus.timeline;
    const stop = tl[1];
    const after = tl[2];
    if (!stop || stop.min !== 0 || stop.stopIdx === 0 || track.cap < stop.d - 1) return undefined;
    if (!after || after.d <= track.cap) return undefined;
    // Con «0» también en la siguiente la API no distingue si ya pasó por las dos o si sigue
    // esperando en esta (la NT de noche da «0» en tres paradas seguidas durante minutos): no
    // sale por su cuenta y espera a la próxima lectura.
    if ((after.min ?? 0) < DEPART_NEXT_MIN) return undefined;
    const reach =
      track.speed > 0 ? shownAt + (1000 * Math.max(0, track.cap - track.base)) / track.speed : shownAt;
    const arrived =
      prev && Math.abs(prev.from - stop.d) < 1
        ? prev.arrived
        : Math.max(reach, timelineAt + stop.t * 1000);
    const leg = after.d - track.cap;
    const dueAfter = timelineAt + after.t * 1000; // llegada a la siguiente según la API
    const at = Math.max(
      arrived + DWELL_S * 1000,
      dueAfter - (1000 * leg) / DEFAULT_INFER.defaultSpeed,
    );
    const speed = Math.max(
      DEFAULT_INFER.minSpeed,
      Math.min(DEFAULT_INFER.maxSpeed, leg / Math.max(1, (dueAfter - at) / 1000)),
    );
    return { at, speed, cap: after.d, from: stop.d, arrived };
  };

  const currentLL = (t: Tracked, now: number): { ll: LatLng; bearing: number } | null => {
    const d = currentD(t, now);
    const p = pointAt(t.entry.geom, d);
    if (!p) return null;
    if (t.blendFrom) {
      const b = t.blendFrom;
      const k = Math.min(1, (now - b.at) / b.ms);
      if (k >= 1) t.blendFrom = null;
      else
        p.ll = {
          lat: b.ll.lat + (p.ll.lat - b.ll.lat) * k,
          lon: b.ll.lon + (p.ll.lon - b.ll.lon) * k,
        };
    }
    return p;
  };

  // Paradas que hay que consultar para unas líneas.
  const stopsOf = (ids: Iterable<LineId>): Set<number> => {
    const out = new Set<number>();
    for (const l of ids)
      for (const e of line(l).routes) for (const s of e.route.stops) out.add(s.id);
    return out;
  };

  // En la cabecera la API anuncia la salida del bus que aún está llegando por el otro sentido:
  // si el que llega puede estar allí para la hora de salida, es el mismo y no se duplica. Se
  // queda el que sale: los minutos de las últimas paradas del que llega van con retraso.
  const dropTurning = (fresh: Fresh[]): void => {
    for (let k = fresh.length - 1; k >= 0; k--) {
      const arr = fresh[k]!;
      const left = endOf(arr.entry).dist - arr.bus.timeline[0]!.d;
      const same = fresh.some((dep) => {
        if (arr === dep || arr.bus.line !== dep.bus.line || !atHead(dep.bus, dep.entry))
          return false;
        if (!turnsInto(arr.entry, dep.entry)) return false;
        const departS = dep.bus.timeline[1]?.t ?? 0;
        return left / DEFAULT_INFER.defaultSpeed <= departS + TURN_SLACK_S;
      });
      if (same) fresh.splice(k, 1);
    }
  };

  // Buses que se deducen de los minutos de una consulta.
  const infer = (
    ids: Iterable<LineId>,
    etas: ReadonlyMap<number, readonly ApiEstimation[]>,
    now?: number,
  ): Fresh[] => {
    const fresh: Fresh[] = [];
    for (const id of ids) {
      const le = line(id);
      const seen = new Set<string>();
      for (const entry of le.routes) {
        const byStop = new Map<number, StopEta[]>();
        for (const s of entry.route.stops) {
          if (now !== undefined && !etas.has(s.id)) continue;
          byStop.set(
            s.id,
            (etas.get(s.id) ?? [])
              .filter((e) => e.line === id)
              .flatMap((e): StopEta[] => {
                if (now === undefined || e.observedAt === undefined) return [e];
                const age = Math.max(0, now - e.observedAt);
                // Un minuto es una banda de llegada, no una medición exacta. Si incluso su
                // extremo superior ya pasó, no reutilizamos esa llegada para crear otro bus.
                if (age > EVIDENCE_STALE_MS || age > (e.minutes + 1) * 60_000) return [];
                return [
                  {
                    ...e,
                    minutes: Math.max(0, e.minutes - age / 60_000),
                    seconds: Math.max(0, (e.minutes === 0 ? 15 : e.minutes * 60 + 30) - age / 1000),
                  },
                ];
              }),
          );
        }
        const buses = inferBuses(entry.route, byStop, {
          ...DEFAULT_INFER,
          excludeDestinations: le.exclude.get(entry.route.key) ?? new Set(),
          requirePreviousObservation: now !== undefined,
        });
        for (const bus of buses) {
          if (now !== undefined) bus.inferredAt = now;
          // Las variantes de una línea comparten tramos: el mismo bus sale en varias.
          const key = `${bus.fromStopId}|${bus.toStopId}`;
          if (seen.has(key)) continue;
          seen.add(key);
          fresh.push({ bus, entry });
        }
      }
    }
    dropTurning(fresh);
    return fresh;
  };

  // Empareja los buses nuevos con los que ya se ven para conservar el id (el marcador no
  // parpadea) y que el movimiento sea continuo:
  //   - mismo trayecto: se compara en metros del trayecto. Si la consulta nueva lo pone detrás,
  //     el bus espera donde está; si lo pone delante, avanza hasta alcanzarla;
  //   - otro trayecto (variante): por cercanía en el mapa y se desliza a su sitio.
  // Un bus que en esta consulta no se ha deducido sigue su camino estimado (MAX_MISSES
  // consultas) en vez de desaparecer.
  const merge = (
    tracked: readonly Tracked[],
    fresh: readonly Fresh[],
    o: MergeOptions,
  ): Tracked[] => {
    const { now, polledAt, nextPollS } = o;
    for (const old of tracked) old.lastLL = currentLL(old, now)?.ll ?? old.lastLL;
    const cands: { i: number; old: Tracked; cost: number }[] = [];
    fresh.forEach((f, i) => {
      const d0 = f.bus.timeline[0]!.d;
      const a = pointAt(f.entry.geom, d0)?.ll ?? null;
      for (const old of tracked) {
        if (old.bus.line !== f.bus.line) continue;
        if (old.bus.sense !== f.bus.sense) {
          // Media vuelta en la cabecera: el que llegaba por un sentido sale por el otro.
          if (!a || !old.lastLL || !atHead(f.bus, f.entry) || !turnsInto(old.entry, f.entry))
            continue;
          if (currentD(old, now) < endOf(old.entry).dist - TURN_NEAR_M * 5) continue;
          const dist = haversine(a, old.lastLL);
          if (dist <= MATCH_MAX_M) cands.push({ i, old, cost: 3 * dist });
          continue;
        }
        if (old.entry.route.key === f.entry.route.key) {
          const delta = d0 - currentD(old, now);
          if (delta >= -BACK_MAX_M && delta <= AHEAD_MAX_M)
            cands.push({ i, old, cost: delta < 0 ? -2 * delta : delta });
        } else if (a && old.lastLL) {
          const dist = haversine(a, old.lastLL);
          if (dist <= MATCH_MAX_M) cands.push({ i, old, cost: 3 * dist });
        }
      }
    });
    cands.sort((x, y) => x.cost - y.cost);
    const usedOld = new Set<Tracked>();
    const matched = new Map<number, Tracked>();
    for (const c of cands) {
      if (usedOld.has(c.old) || matched.has(c.i)) continue;
      usedOld.add(c.old);
      matched.set(c.i, c.old);
    }
    const out: Tracked[] = fresh.map((f, i) => {
      const old = matched.get(i);
      const d0 = f.bus.timeline[0]!.d;
      const timelineAt = f.bus.inferredAt ?? polledAt;
      const evidenceAt = f.bus.observedAt ?? polledAt;
      const renewed =
        !old ||
        (f.bus.evidenceKey === undefined
          ? evidenceAt > (old.lastEvidenceAt ?? old.polledAt)
          : f.bus.evidenceKey !== old.bus.evidenceKey &&
            evidenceAt > (old.lastEvidenceAt ?? old.polledAt));
      // Recalcular la misma respuesta cacheada no es una observación independiente: mantiene
      // su trayectoria, su confirmación y su instante originales.
      if (old && !renewed) return { ...old, misses: 0 };
      const t: Tracked = {
        id: old?.id ?? o.newId(),
        bus: f.bus,
        entry: f.entry,
        polledAt: timelineAt,
        shownAt: now,
        track: null,
        obsD: d0,
        obsT: timelineAt,
        blendFrom: null,
        lastLL: old?.lastLL ?? null,
        misses: 0,
        // Saliendo de la cabecera es normal que aparezca: ahí no hace falta confirmarlo.
        shown:
          !!old?.shown ||
          (!!old && renewed && now - (old.firstSeen ?? 0) >= (o.confirmMs ?? 0)) ||
          !o.polledLines.has(f.bus.line) ||
          f.bus.fromStopId === f.entry.route.stops[0]?.id,
        firstSeen: old ? (old.firstSeen ?? 0) : now,
        confidence: old && renewed && now - evidenceAt <= EXTRAPOLATE_MS ? "medium" : "low",
        lastEvidenceAt: evidenceAt,
      };
      // Recién visto (o cambio de variante): avanza por lo que dice la API hasta donde estará en
      // la consulta siguiente. Solo hasta su próxima parada lo dejaba quieto casi toda la espera
      // entre consultas (con muchas líneas, más de un minuto).
      // No pasa de su próxima parada hasta que la API diga que la ha dejado atrás: mejor que
      // el mapa vaya unos segundos tarde que enseñar ido un bus que aún no ha llegado. Con
      // «0 min» se pone ya en ella, para DWELL_S y sigue hacia la siguiente (sin pasarla).
      const next = f.bus.timeline[1];
      const nextD = next?.d ?? d0;
      const arriving = next?.min === 0;
      const tNext = (now - timelineAt) / 1000 + nextPollS;
      const capD = Math.min(distanceAt(f.bus.timeline, tNext), nextD);
      if (capD > d0 && tNext > 0) {
        const v = arriving
          ? Math.max((capD - d0) / tNext, (capD - d0) / AT_STOP_S)
          : (capD - d0) / tNext;
        t.track = { base: d0, speed: Math.min(DEFAULT_INFER.maxSpeed, v), cap: capD };
        t.shownAt = arriving ? now : timelineAt;
        if (arriving) {
          const prev = old?.entry.route.key === f.entry.route.key ? old.track?.then : undefined;
          const then = departure(f, t.track, t.shownAt, timelineAt, prev);
          if (then) t.track.then = then;
        }
      }
      if (!old) return t;
      const blend = (): void => {
        if (!old.lastLL) return;
        const to = pointAt(f.entry.geom, d0)?.ll;
        const m = to ? haversine(old.lastLL, to) : 0;
        t.blendFrom = {
          ll: old.lastLL,
          at: now,
          ms: Math.max(BLEND_MS, (1000 * m) / BLEND_MS_SPEED),
        };
      };
      if (old.entry.route.key !== f.entry.route.key) {
        blend();
        return t;
      }
      // A mitad de un deslizamiento sigue deslizándose desde donde se ve.
      if (old.blendFrom) blend();
      t.shownAt = now;
      // Dónde estará en la próxima consulta: lo deducido ahora más lo que avanza de verdad
      // (observado entre consultas; la línea temporal de la API suele ser optimista).
      const was = currentD(old, now);
      const dt = (timelineAt - old.obsT) / 1000;
      // Velocidad suavizada en el tiempo. Los minutos de la API son enteros: entre dos lecturas
      // con el mismo minuto la posición deducida casi no cambia y la velocidad «observada» de
      // esa pareja sale 0 (y luego, de golpe, muy alta). Usada tal cual, el bus se paraba y
      // arrancaba a tirones.
      const observed = dt > 5 ? Math.max(0, (d0 - old.obsD) / dt) : null;
      const prevV = old.vEst ?? DEFAULT_INFER.defaultSpeed;
      const vEst =
        observed === null
          ? prevV
          : prevV +
            (1 - Math.exp(-dt / SPEED_TAU_S)) *
              (Math.min(DEFAULT_INFER.maxSpeed, observed) - prevV);
      t.vEst = vEst;
      const ahead = (now - timelineAt) / 1000 + nextPollS;
      const target = d0 + vEst * ahead;
      // Dentro del margen de un minuto entero (±BAND_S a su velocidad) sigue a su ritmo; por
      // delante de él frena, muy por delante espera; por detrás alcanza la posición deducida.
      const band = BAND_S * vEst;
      let speed: number;
      if (was > target + band) speed = 0;
      else if (was > target) speed = vEst * ((target + band - was) / band);
      else speed = Math.max(vEst, (target - was) / nextPollS);
      speed = Math.min(DEFAULT_INFER.maxSpeed, speed);
      if (arriving)
        speed = Math.min(DEFAULT_INFER.maxSpeed, Math.max(speed, (nextD - was) / AT_STOP_S));
      // Una velocidad cero es válida. No inventamos avance para disimular la falta de datos.
      t.track = { base: was, speed, cap: Math.max(was, nextD) };
      if (arriving) {
        const then = departure(f, t.track, now, timelineAt, old.track?.then);
        if (then) t.track.then = then;
      }
      return t;
    });
    for (const old of tracked) {
      if (
        usedOld.has(old) ||
        !old.shown ||
        old.misses + 1 > MAX_MISSES ||
        !o.active.has(old.bus.line)
      )
        continue;
      // Llegó al final del trayecto: ya no circula por él (si sigue, sale en el otro sentido).
      if (currentD(old, now) >= endOf(old.entry).dist - 30) continue;
      // Con otro del mismo trayecto casi encima, era un duplicado de ese: fuera ya.
      const here = currentD(old, now);
      if (
        out.some(
          (t) =>
            t.bus.routeKey === old.bus.routeKey && Math.abs(currentD(t, now) - here) < NEAR_TWIN_M,
        )
      )
        continue;
      out.push({ ...old, misses: old.misses + 1, confidence: "low" });
    }
    return out;
  };

  // Minutos del próximo bus de `line` en la parada, en el sentido del trayecto `routeId`.
  const nextAt = (
    etas: ReadonlyMap<number, readonly ApiEstimation[]>,
    stopId: number,
    lineId: LineId,
    routeId: number,
  ): number | null => {
    const key = `${lineId}|${routeId}`;
    const exclude = line(lineId).exclude.get(key);
    const now = Date.now();
    const list = (etas.get(stopId) ?? []).filter(
      (e) =>
        e.line === lineId &&
        !(exclude?.has(normDest(e.destination)) ?? false) &&
        (e.observedAt === undefined ||
          (now - e.observedAt <= EVIDENCE_STALE_MS &&
            now - e.observedAt <= (e.minutes + 1) * 60_000)),
    );
    if (list.length === 0) return null;
    return Math.min(
      ...list.map((e) =>
        e.observedAt === undefined
          ? e.minutes
          : Math.max(0, Math.ceil(e.minutes - Math.max(0, now - e.observedAt) / 60_000)),
      ),
    );
  };

  const toState = ({ entry: _e, ...s }: Tracked): BusState => s;
  const fromState = (s: BusState): Tracked | null => {
    const entry = entryOf(s.bus.routeKey);
    return entry ? { ...s, entry } : null;
  };

  return { line, entryOf, stopsOf, infer, merge, currentD, currentLL, nextAt, toState, fromState };
}
